import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  X,
  Loader2,
  Copy,
  Check,
  Stethoscope,
  Heart,
  Users,
  ShieldAlert,
  Link2,
} from "lucide-react";

const KINDS = [
  { key: "doctor", label: "Doctor", Icon: Stethoscope },
  { key: "family", label: "Family", Icon: Heart },
  { key: "other", label: "Other", Icon: Users },
];

const TTL_OPTIONS = [
  { label: "24 hours", hours: 24 },
  { label: "3 days", hours: 72 },
  { label: "7 days", hours: 168 },
  { label: "14 days", hours: 336 },
  { label: "30 days", hours: 720 },
];

/**
 * Modal for creating a share-link to send a single report to a doctor / family / other.
 * @param {{ reportId: string, clientName: string, onClose: () => void }} props
 */
export default function ShareReportDialog({ reportId, clientName, onClose }) {
  const [kind, setKind] = useState("doctor");
  const [recipient, setRecipient] = useState("");
  const [ttl, setTtl] = useState(168);
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/reports/${reportId}/share`, {
        recipient_kind: kind,
        recipient_name: recipient.trim(),
        note: note.trim(),
        ttl_hours: ttl,
      });
      setCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create share link");
    } finally {
      setCreating(false);
    }
  };

  const shareUrl = created
    ? `${window.location.origin}/share/report/${created.token}`
    : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const mailto = (() => {
    if (!created) return "";
    const subj = encodeURIComponent(`Shift report for ${clientName}`);
    const recName = recipient ? `${recipient}, ` : "";
    const body = encodeURIComponent(
      `Hi ${recName}\n\n` +
      `Please find a shift report for ${clientName}. ` +
      `This is a private, view-only link from Afyanex Care. ` +
      `It will expire on ${new Date(created.expires_at).toLocaleString()}.\n\n` +
      `${shareUrl}\n\n` +
      `${note ? note + "\n\n" : ""}Thanks.`
    );
    return `mailto:?subject=${subj}&body=${body}`;
  })();

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={onClose}
      data-testid="share-report-modal"
    >
      <div
        className="bg-white sm:rounded-2xl border-t sm:border border-clinical-border w-full sm:max-w-md max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-clinical-border">
          <div>
            <div className="label-eyebrow">Share report</div>
            <h3 className="font-heading text-lg font-medium">{clientName}</h3>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost !p-1.5" data-testid="share-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {!created ? (
          <>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs text-clinical-subtext block mb-1.5">Recipient kind</label>
                <div className="grid grid-cols-3 gap-2">
                  {KINDS.map((k) => {
                    const active = kind === k.key;
                    return (
                      <button
                        key={k.key}
                        type="button"
                        onClick={() => setKind(k.key)}
                        className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-3 text-sm transition-colors ${
                          active
                            ? "border-clinical-primary bg-clinical-primary/5 text-clinical-primary"
                            : "border-clinical-border text-clinical-subtext hover:border-clinical-primary/40"
                        }`}
                        data-testid={`share-kind-${k.key}`}
                      >
                        <k.Icon className="h-4 w-4" />
                        {k.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-xs text-clinical-subtext block mb-1">
                  Recipient name <span className="text-clinical-subtext/70">(optional, shown in the email)</span>
                </label>
                <input
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder={kind === "doctor" ? "Dr. Smith" : kind === "family" ? "Lisa (daughter)" : ""}
                  className="input-clinical"
                  data-testid="share-recipient-input"
                />
              </div>
              <div>
                <label className="text-xs text-clinical-subtext block mb-1.5">Expires after</label>
                <div className="grid grid-cols-5 gap-1.5">
                  {TTL_OPTIONS.map((o) => (
                    <button
                      key={o.hours}
                      type="button"
                      onClick={() => setTtl(o.hours)}
                      className={`rounded-md px-2 py-2 text-[11px] font-medium transition-colors ${
                        ttl === o.hours
                          ? "bg-clinical-primary text-white"
                          : "border border-clinical-border text-clinical-subtext hover:border-clinical-primary/40"
                      }`}
                      data-testid={`share-ttl-${o.hours}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-clinical-subtext block mb-1">
                  Personal note <span className="text-clinical-subtext/70">(optional)</span>
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  placeholder="e.g. Please review the BP trend; she felt dizzy this morning."
                  className="input-clinical resize-y"
                  data-testid="share-note-input"
                  maxLength={300}
                />
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
                Anyone with this link can view the full report (no login required). The link
                expires automatically and can be revoked at any time.
              </div>
            </div>
            <div className="sticky bottom-0 bg-white flex items-center justify-end gap-2 px-5 py-4 border-t border-clinical-border">
              <button type="button" onClick={onClose} className="btn-secondary" data-testid="share-cancel">Cancel</button>
              <button
                type="button"
                onClick={create}
                disabled={creating}
                className="btn-primary"
                data-testid="share-create-button"
              >
                {creating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Creating link…
                  </>
                ) : (
                  <>
                    <Link2 className="h-4 w-4" /> Create share link
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          <div className="p-5 space-y-4" data-testid="share-result">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-900">
              ✓ Share link created. Expires {new Date(created.expires_at).toLocaleString()}.
            </div>
            <div>
              <label className="text-xs text-clinical-subtext block mb-1">Link</label>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={shareUrl}
                  className="input-clinical font-mono text-[11px]"
                  data-testid="share-link-input"
                  onFocus={(e) => e.target.select()}
                />
                <button
                  type="button"
                  onClick={copyLink}
                  className="btn-secondary !px-3"
                  data-testid="share-copy-button"
                >
                  {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="btn-secondary" data-testid="share-done">
                Done
              </button>
              <a
                href={mailto}
                className="btn-primary"
                data-testid="share-email-button"
              >
                Open email
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
